import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Flex, IconButton, Text, useMediaQuery,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  LuCheck, LuClock, LuEllipsis, LuTriangleAlert, LuX,
} from 'react-icons/lu';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { formatMoney, formatPhoneMoney, formatRowAmount } from '../../utils/formatters';
import { getAccountDisplay, getPrimaryText } from '../../utils/movementDisplay';
import PhoneSheet from './PhoneSheet';
import { CategorySheet, CategoryTile, TileGrid } from './PhonePickers';
import { usePhoneShell } from './PhoneShellContext';
import {
  ReasonLine, ReviewDisc, WaitingRow, readFixedToday, reviewDateLabel, reviewFix, useCategorySuggestions,
} from './reviewFixes';

// Seven tiles and Other…, in two rows. A short screen keeps one row: 3 and
// Other… upright, 5 and Other… in landscape, whose grid is wider (six fit
// between the sensor housing's insets even on a 667px-wide phone).
const FOCUS_TILES = 7;
const FOCUS_TILES_SHORT = 3;
const FOCUS_TILES_LANDSCAPE = 5;
// Below these heights the card shrinks, and the actions move to a footer
// that never scrolls away, so every action stays on screen.
const SHORT_QUERY = '(max-height: 699.98px)';
const TINY_QUERY = '(max-height: 599.98px)';
const WIDE_QUERY = '(min-width: 560px)';

/**
 * "−COP 45,000" or "−USD 8.00": the sign from the type and the code always;
 * the home currency drops a zero fraction, as on the rows.
 */
export function signedMoney(movement, home) {
  const { tone } = formatRowAmount(movement, movement.currency);
  const sign = tone === 'income' ? '+' : tone === 'expense' ? '−' : '';
  const abs = Math.abs(Number(movement.amount) || 0);
  return `${sign}${movement.currency === home ? formatPhoneMoney(movement.currency, abs) : formatMoney(abs, movement.currency)}`;
}

/** The "Other…" tile: every category of the type, in a sheet. */
export function OtherTile({ onClick, label }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="4px"
      minH="76px"
      minW={0}
      px="4px"
      borderRadius="12px"
      bg="redesign.bg.card"
      border="1.5px solid"
      borderColor="redesign.border"
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <Flex w="32px" h="32px" align="center" justify="center" color="phone.muted">
        <Box as={LuEllipsis} boxSize="20px" />
      </Flex>
      <Box as="span" fontSize="11px" lineHeight="13px" color="redesign.heading">{label}</Box>
    </Box>
  );
}

/**
 * The end of the pass. "All caught up" only when nothing is left: cards put
 * off with Later are still to fix, so then it says how many wait for later.
 */
function AllCaughtUp({
  waiting, home, onBack, headingRef, laterCount, backInFooter,
}) {
  const { t } = useTranslation('movements');
  const { currentId } = useWorkspace();
  const { openMovement } = usePhoneShell();
  const fixed = readFixedToday(currentId);
  const clear = laterCount === 0;
  return (
    <Flex direction="column" flex="1">
      <Flex direction="column" align="center" pt="40px" px="16px" textAlign="center">
        <Flex
          w="72px"
          h="72px"
          borderRadius="full"
          bg={clear ? 'rgba(21, 128, 61, 0.10)' : 'redesign.bg.card'}
          _dark={clear ? { bg: 'rgba(74, 222, 128, 0.14)' } : undefined}
          color={clear ? 'phone.income' : 'phone.muted'}
          align="center"
          justify="center"
        >
          <Box as={clear ? LuCheck : LuClock} boxSize="34px" />
        </Flex>
        <Text as="h2" mt="16px" fontSize="24px" fontWeight="700" letterSpacing="-0.02em" color="redesign.heading" tabIndex={-1} ref={headingRef} _focus={{ outline: 'none' }}>
          {clear ? t('review.caughtUp') : t('review.leftForLater', { count: laterCount })}
        </Text>
        {fixed > 0 && (
          <Text mt="4px" fontSize="15px" color="phone.muted">{t('review.fixedToday', { count: fixed })}</Text>
        )}
      </Flex>
      {waiting.length > 0 && (
        <Box mt="28px">
          <Text as="h3" px="16px" pb="8px" fontSize="12px" lineHeight="16px" fontWeight="600" letterSpacing="0.05em" textTransform="uppercase" color="phone.muted">
            {t('review.waitingTitle', { count: waiting.length })}
          </Text>
          {waiting.map((m) => (
            <WaitingRow key={m.id} movement={m} home={home} onClick={() => openMovement(m.id)} />
          ))}
          <Text px="16px" pt="10px" fontSize="13px" lineHeight="1.4" color="phone.muted">{t('review.waitingNote')}</Text>
        </Box>
      )}

      {!backInFooter && (
        <>
          <Box flex="1" />
          <Box px="16px" pt="16px">
            <Button variant="phonePrimary" w="100%" onClick={onBack}>{t('review.backHome')}</Button>
          </Box>
        </>
      )}
    </Flex>
  );
}

/**
 * Review one movement at a time: the movement large, its reason, and every
 * action in the bottom third, in thumb reach. Any resolving tap moves on;
 * Undo steps back one card and reverts what was done to it. At the end,
 * "All caught up".
 *
 * `queue` is Review's `useReviewQueue`. The order is fixed when the view
 * opens; a movement fixed elsewhere (in the movement sheet) counts as done.
 */
export default function ReviewFocus({ isOpen, onClose, onBackHome, queue }) {
  const { t } = useTranslation('movements');
  const { openMovement } = usePhoneShell();
  const [order, setOrder] = useState([]);
  const [done, setDone] = useState({});        // id -> 'fixed' | 'later'
  const [history, setHistory] = useState([]);  // [{ id, kind, undo }]
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);  // { type, switchTo }
  const nameRef = useRef(null);
  const [short, tiny, wide] = useMediaQuery([SHORT_QUERY, TINY_QUERY, WIDE_QUERY], { ssr: false });
  // In landscape Open and Later share a row, under a single row of tiles.
  const sideBySide = tiny && wide;

  // The snapshot the progress counts through, taken as the view opens.
  useEffect(() => {
    if (isOpen) {
      setOrder(queue.active);
      setDone({});
      setHistory([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fixableIds = useMemo(() => new Set(queue.fixable.map((m) => m.id)), [queue.fixable]);
  const isDone = (m) => Boolean(done[m.id]) || (!queue.loading && !fixableIds.has(m.id));
  const current = order.find((m) => !isDone(m));
  const movement = current ? (queue.fixable.find((m) => m.id === current.id) || current) : null;
  const position = order.filter(isDone).length + (movement ? 1 : 0);
  // The bar fills to the card shown, as the "2 / 4" beside it counts.
  const progress = order.length ? (movement ? position : order.length) / order.length : 1;

  const reasons = movement ? queue.reasonsOf(movement) : [];
  const fix = movement ? reviewFix(movement, reasons, queue.accounts) : null;
  const { tiles, recentIds } = useCategorySuggestions(
    fix?.kind === 'category' ? movement : null,
    queue.categories,
    sideBySide ? FOCUS_TILES_LANDSCAPE : tiny ? FOCUS_TILES_SHORT : FOCUS_TILES,
    queue.noteHistory,
  );
  const laterCount = order.filter((m) => done[m.id] === 'later' && fixableIds.has(m.id)).length;

  // VoiceOver lands on the new card's name at every advance.
  const currentId = movement?.id ?? 'end';
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => nameRef.current?.focus(), 350);
    return () => clearTimeout(id);
  }, [currentId, isOpen]);

  const resolveWith = async (payload) => {
    if (busy || !movement) return;
    setBusy(true);
    const result = await queue.applyFix(movement, payload, null, { quiet: true });
    setBusy(false);
    if (!result) return;
    setDone((d) => ({ ...d, [movement.id]: 'fixed' }));
    setHistory((h) => [...h, { id: movement.id, kind: 'fixed', undo: result.undo }]);
  };

  const later = () => {
    if (!movement) return;
    queue.snooze(movement.id);
    setDone((d) => ({ ...d, [movement.id]: 'later' }));
    setHistory((h) => [...h, { id: movement.id, kind: 'later' }]);
  };

  const undo = async () => {
    const last = history[history.length - 1];
    if (!last || busy) return;
    setBusy(true);
    if (last.kind === 'fixed') {
      const ok = await last.undo();
      if (!ok) { setBusy(false); return; }
    } else {
      queue.unsnooze(last.id);
    }
    setBusy(false);
    setHistory((h) => h.slice(0, -1));
    setDone((d) => {
      const next = { ...d };
      delete next[last.id];
      return next;
    });
  };

  const pickCategory = (category) => {
    if (!category || !movement) return;
    resolveWith(picker?.switchTo
      ? { type: picker.switchTo, category_id: category.id }
      : { category_id: category.id });
  };

  const canFix = queue.canFix;
  let primary = null;
  if (canFix && fix?.kind === 'switch') {
    primary = (
      <Button variant="phonePrimary" isDisabled={busy} onClick={() => setPicker({ type: fix.to, switchTo: fix.to })}>
        {t(fix.to === 'Income' ? 'review.actions.switchToIncome' : 'review.actions.switchToExpense')}
      </Button>
    );
  } else if (canFix && fix?.kind === 'move') {
    primary = (
      <Button variant="phonePrimary" isDisabled={busy} onClick={() => resolveWith({ account_id: fix.account.id })}>
        {t('review.actions.moveTo', { account: fix.account.name })}
      </Button>
    );
  }

  const actions = movement ? (
    <Flex direction="column" gap="10px" px={short ? 0 : '16px'} flexShrink={0}>
      {canFix && fix?.kind === 'category' && (
        <Box mx="-16px" mb="2px" sx={{ '& span': { hyphens: 'auto', wordBreak: 'normal', overflowWrap: 'break-word' } }}>
          <TileGrid>
            {tiles.map((c) => (
              <CategoryTile key={c.id} category={c} onClick={() => resolveWith({ category_id: c.id })} />
            ))}
            <OtherTile label={t('review.actions.other')} onClick={() => setPicker({ type: movement.type })} />
          </TileGrid>
        </Box>
      )}
      {primary}
      <Flex direction={sideBySide ? 'row' : 'column'} gap={sideBySide ? '8px' : '10px'}>
        <Button
          variant="outline"
          flex={sideBySide ? '1' : undefined}
          h={tiny ? '44px' : '52px'}
          fontSize="17px"
          fontWeight="600"
          borderRadius="12px"
          borderColor="redesign.border"
          color="redesign.heading"
          onClick={() => openMovement(movement.id)}
        >
          {t('review.actions.openMovement')}
        </Button>
        <Button
          variant="ghost"
          flex={sideBySide ? '1' : undefined}
          h="44px"
          fontSize="17px"
          color="redesign.text"
          _hover={{ bg: 'transparent' }}
          isDisabled={busy}
          onClick={later}
        >
          {t('review.actions.later')}
        </Button>
      </Flex>
    </Flex>
  ) : (
    <Button variant="phonePrimary" w="100%" onClick={onBackHome}>{t('review.backHome')}</Button>
  );

  return (
    <PhoneSheet
      id="review-focus"
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={movement ? `${position} / ${order.length}` : ''}
      headerLeft={(
        <IconButton
          icon={<LuX size={22} />}
          variant="ghost"
          borderRadius="full"
          minW="44px"
          minH="44px"
          color="redesign.text"
          aria-label={t('common:actions.close')}
          onClick={onClose}
        />
      )}
      // Undo stays after the last card too: it steps back into that card.
      headerRight={history.length > 0 ? (
        <Button variant="ghost" color="phone.link" fontSize="16px" fontWeight="600" minH="44px" px="12px" isDisabled={busy} onClick={undo}>
          {t('review.undo')}
        </Button>
      ) : null}
      footer={short ? actions : undefined}
    >
      <Flex direction="column" minH="100%" pb="8px">
        <Box mx="16px" mt="4px" h="4px" borderRadius="2px" bg="redesign.border" flexShrink={0}>
          <Box h="4px" borderRadius="2px" bg="redesign.accent.blue" w={`${progress * 100}%`} transition="width 0.25s ease-out" />
        </Box>

        {!movement ? (
          <Box flex="1" display="flex" flexDirection="column">
            <AllCaughtUp
              waiting={queue.waiting}
              home={queue.home}
              onBack={onBackHome}
              headingRef={nameRef}
              laterCount={laterCount}
              backInFooter={short}
            />
          </Box>
        ) : (
          <>
            <Flex flex="1" direction="column" justify="center" align="center" gap={short ? '10px' : '14px'} px="16px" py={short ? '10px' : '16px'}>
              <Flex align="center" gap="5px" h="26px" px="10px" borderRadius="full" bg="phone.alertBg" color="phone.alert" fontSize="12px" fontWeight="600">
                <Box as={LuTriangleAlert} boxSize="14px" />
                {t('review.alertPill')}
              </Flex>
              <Flex
                alignSelf="stretch"
                direction="column"
                align="center"
                textAlign="center"
                px={short ? '16px' : '20px'}
                pt={short ? '14px' : '24px'}
                pb={short ? '14px' : '22px'}
                borderRadius="16px"
                bg="redesign.bg.card"
                border="1px solid"
                borderColor="redesign.border"
              >
                <ReviewDisc movement={movement} size={short ? 40 : 56} />
                <Text
                  ref={nameRef}
                  tabIndex={-1}
                  mt={short ? '8px' : '14px'}
                  fontSize={short ? '18px' : '22px'}
                  lineHeight="1.25"
                  fontWeight="600"
                  letterSpacing="-0.02em"
                  color="redesign.heading"
                  wordBreak="break-word"
                  _focus={{ outline: 'none' }}
                >
                  {getPrimaryText(movement)}
                </Text>
                <Text
                  textStyle={short ? 'phoneTitle' : 'phoneHero'}
                  mt={short ? '2px' : '6px'}
                  color="redesign.heading"
                  whiteSpace="nowrap"
                >
                  {signedMoney(movement, queue.home)}
                </Text>
                <Text mt={short ? '4px' : '8px'} fontSize="14px" color="phone.muted">
                  {[getAccountDisplay(movement), reviewDateLabel(movement.date, t)].filter(Boolean).join(' · ')}
                </Text>
                <Box alignSelf="stretch" h="1px" bg="redesign.border" mt={short ? '12px' : '18px'} mb={short ? '10px' : '16px'} />
                <Flex direction="column" gap="6px" alignSelf="stretch">
                  {reasons.map((r) => <ReasonLine key={r.flag} text={r.text} fontSize="15px" />)}
                </Flex>
              </Flex>
            </Flex>

            {!short && actions}
          </>
        )}
      </Flex>

      <CategorySheet
        isOpen={Boolean(picker)}
        onClose={() => setPicker(null)}
        categories={queue.categories}
        type={picker?.type}
        // The movement's own category, so the sheet never ticks a false "None".
        value={movement?.category_id ?? null}
        recentIds={picker?.switchTo ? [] : recentIds}
        onChange={pickCategory}
      />
    </PhoneSheet>
  );
}
